import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StudentService } from 'src/app/services/student.service';
import { Student } from 'src/app/classes/model/student';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { StudentDetailsDialogComponent, StudentDetailsDialogData } from './student-details-dialog/student-details-dialog.component';
import { FormControl } from '@angular/forms';
import { MatSort, Sort } from '@angular/material/sort';
import { debounceTime, Subscription, Subject, switchMap, from, of, catchError, takeUntil } from 'rxjs';
import { StudentPaginationParams, PaginatedStudentsResponse } from 'src/app/classes/model/student-pagination';

@Component({
  selector: 'app-students',
  templateUrl: './students.component.html',
  styleUrls: ['./students.component.scss']
})
export class StudentsComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator:MatPaginator;
  @ViewChild(MatSort,  { static: true }) sort:MatSort;

  stundentSearchFC:FormControl<string>;

  studentLoadingInProgress:boolean = false;

  dataSource:MatTableDataSource<Readonly<Student>>;
  displayedColumns:ReadonlyArray<string> = [ "uuid", "fullname", "email", "customerId", "registrationDate" ];

  // Pagination state
  totalItems: number = 0;
  currentPage: number = 1;
  pageSize: number = 10;
  sortOrder: 'asc' | 'desc' = 'desc';

  // Request management
  private destroy$ = new Subject<void>();
  private loadStudentsSubject$ = new Subject<void>();
  private loadStudentsSubscription: Subscription;

  constructor(
    private studentService:StudentService,
    public dialog:MatDialog,

  ) { }

  public ngOnInit():void {
    // Create the datasource for the material table
    this.dataSource = new MatTableDataSource<Readonly<Student>>();
    this.dataSource.data = [];

    // Create the form control
    this.stundentSearchFC = new FormControl<string>("");

    // Subscribe to form value changes
    this.stundentSearchFC.valueChanges.pipe(
      debounceTime(600),
      takeUntil(this.destroy$)
    ).subscribe(
      () => {
        // Reset to first page when searching
        this.currentPage = 1;
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
        this.triggerLoadStudents();
      }
    );

    // Set up the main loading subscription with switchMap to cancel previous requests
    this.loadStudentsSubscription = this.loadStudentsSubject$.pipe(
      switchMap(() => {
        this.studentLoadingInProgress = true;
        return from(this.performLoadStudents()).pipe(
          catchError((error) => {
            console.error('Error loading students:', error);
            this.dataSource.data = [];
            this.totalItems = 0;
            this.studentLoadingInProgress = false;
            return of(null);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((response) => {
      this.studentLoadingInProgress = false;
      if (response) {
        this.handleLoadStudentsResponse(response);
      }
    });

    // Configure paginator
    this.paginator.pageSize = this.pageSize;
    this.paginator.pageSizeOptions = [10, 25, 100];

    // Load initial data
    this.triggerLoadStudents();
  }

  public ngOnDestroy():void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadStudentsSubscription?.unsubscribe();
  }

  private triggerLoadStudents(): void {
    this.loadStudentsSubject$.next();
  }

  private async performLoadStudents(): Promise<PaginatedStudentsResponse | null> {
    const searchValue = this.stundentSearchFC.value?.trim() || '';

    // Prepare pagination parameters
    const params: StudentPaginationParams = {
      page: this.currentPage,
      limit: this.pageSize,
      sortOrder: this.sortOrder
    };

    // Add search filters if present
    if (searchValue) {
      // Always add as name search
      params.nameContains = searchValue;
      
      params.emailContains = searchValue;
      
      // Check if search value looks like a customer ID (e.g., XX-XX format)
      if (/^[A-Z]{2}-[0-9]{2}$/i.test(searchValue)) {
        params.customerIdExact = searchValue;
      }
      
      // Check if search value could be a UUID (partial or full)
      if (searchValue.length >= 3 && /^[0-9a-f-]+$/i.test(searchValue)) {
        params.uuid = searchValue;
      }
    }

    return await this.studentService.fetchStudentsPaginated(params);
  }

  private handleLoadStudentsResponse(response: PaginatedStudentsResponse): void {
    this.dataSource.data = response.items;
    this.totalItems = response.totalItems;

    // Update paginator
    if (this.paginator) {
      this.paginator.length = this.totalItems;
      this.paginator.pageIndex = response.currentPage - 1; // Material paginator is 0-based
      this.paginator.pageSize = response.itemsPerPage;
    }
  }

  public onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1; // Convert from 0-based to 1-based
    this.pageSize = event.pageSize;
    this.triggerLoadStudents();
  }

  public onSortChange(sort: Sort): void {
    this.sortOrder = sort.direction as 'asc' | 'desc';
    // Reset to first page when sorting
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.triggerLoadStudents();
  }

  public async openStudentDetailsDialog(student:Readonly<Student>):Promise<void> {
    this.dialog.open<StudentDetailsDialogComponent, StudentDetailsDialogData>(
      StudentDetailsDialogComponent,
      {
        data: {
          studentUuid: student.uuid
        }
      }
    );
  }

  public async handleReloadButtonClick():Promise<void> {
    // Reset to first page and reload
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.triggerLoadStudents();
  }

  // Legacy method - keeping for compatibility but not using caching anymore
  public async loadStudents(refreshStudents:boolean = true):Promise<void> {
    // Redirect to new loading method
    this.triggerLoadStudents();
  }

  // Deprecated - use triggerLoadStudents instead
  public async loadStudentsPaginated():Promise<void> {
    this.triggerLoadStudents();
  }

  protected getCustomerIdOfStudent(student:Student):string {
    if(!student.customerId?.id) {
      return "-"
    }

    const separatorPosition:number = student.customerId.id.length / 2;
    return `${student.customerId.id.substring(0, separatorPosition)}-${student.customerId.id.substring(separatorPosition,)}`;
  }
}
